import { FunctionComponent, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { DAY_FORMAT, Expiry } from '../../../models/constants'
import DateTimePickerComponent from '@/components/_custom/Picker/dateTimePicker/dateTimePicker'
import SelectUnique from '@/components/_custom/Selector/selectUnique/selectUnique'
import { ExpirationDuration } from '@/types/Daycare'
import { SelectOptionsDeprecated } from '@/types/Select'
import { useCurrentDaycareInfos } from '@/hooks/useCurrentDaycareInfos'
import styles from '../../occasionalSlotForm.module.scss'
import moment from 'moment'
import { useDialog } from '@/hooks/useDialog/useDialog'
import OtherReservationSettings from '@/container/daycare/daycareProfile/merger/otherReservationSettings/otherReservationSettings'
import { RefObject } from '@fullcalendar/core/preact.js'

interface ExpiranceFieldProps {
    expirance: Expiry
    setExpirance(expirance: Expiry): void
    minDate: string
    maxDate: string
}

const ExpiranceField: FunctionComponent<ExpiranceFieldProps> = ({
    expirance,
    setExpirance,
    minDate,
    maxDate
}: ExpiranceFieldProps) => {
    const { t } = useTranslation()
    const { currentDaycare } = useCurrentDaycareInfos()

    const { dialog, openDialogActionItem } = useDialog({
        dialogTitle: t('otherReservationsSettings'),
        Component: OtherReservationSettings,
        props: (onClose) => ({ onClose, visibleSettings: ['expiration'] }),
        resourceLabel: { type: 'manageResource', label: t('myDefaultExpiration') }
    })

    const SELECT_DATE: string = 'select_date'

    const daycareExpirationDurations: SelectOptionsDeprecated = [
        {
            options: [
                {
                    name: t('expiration0'),
                    value: ExpirationDuration.expiration0
                },
                {
                    name: t('selectDate'),
                    value: SELECT_DATE
                },
                {
                    name: t('expiration1'),
                    value: ExpirationDuration.expiration1
                },
                {
                    name: t('expiration12'),
                    value: ExpirationDuration.expiration12
                },
                {
                    name: t('expiration18'),
                    value: ExpirationDuration.expiration18
                },
                {
                    name: t('expiration24'),
                    value: ExpirationDuration.expiration24
                },
                {
                    name: t('expiration48'),
                    value: ExpirationDuration.expiration48
                },
                {
                    name: t('expiration72'),
                    value: ExpirationDuration.expiration72
                },
                {
                    name: t('expiration_168'),
                    value: ExpirationDuration.expiration168
                }
            ]
        }
    ]

    const defaultExpiration: RefObject<ExpirationDuration> = useRef(currentDaycare.expirationDuration)

    useEffect(() => {
        const newDefaultExpiration: ExpirationDuration = currentDaycare.expirationDuration

        if (newDefaultExpiration !== defaultExpiration.current) {
            defaultExpiration.current = newDefaultExpiration
            setExpirance({
                type: 'duration',
                expiryDuration: newDefaultExpiration
            })
        }
    }, [currentDaycare.expirationDuration])

    function handleDurationChange(newDuration: string) {
        if (newDuration === SELECT_DATE) {
            return setExpirance({ type: 'date', expiryDate: maxDate })
        } else {
            return setExpirance({ type: 'duration', expiryDuration: newDuration as ExpirationDuration })
        }
    }

    function handleDateChange(newDate: string) {
        return setExpirance({ type: 'date', expiryDate: newDate })
    }

    switch (expirance.type) {
        case 'date':
            return (
                <DateTimePickerComponent
                    value={expirance.expiryDate || moment().format(DAY_FORMAT)}
                    setValue={handleDateChange}
                    className={styles.UserInput}
                    label={t('expirationDate')}
                    minuteToDisable={
                        currentDaycare.merger ? Number(currentDaycare.bookRange.split('_')[1] || '15') : 15
                    }
                    minDate={minDate}
                    maxDate={maxDate}
                />
            )
        case 'duration':
            return (
                <>
                    <SelectUnique
                        selectOptions={daycareExpirationDurations}
                        selectValue={expirance.expiryDuration}
                        setSelectValue={handleDurationChange}
                        formClassName={styles.UserInput}
                        label={t('expirationAuto')}
                        actionItem={openDialogActionItem}
                    />
                    {dialog}
                </>
            )
    }
}

export default ExpiranceField
